'use client';

import { useState, useCallback, useMemo, useEffect } from 'react';
import cn from 'classnames';
import noop from 'lodash/noop';
import throttle from 'lodash/throttle';
import { CONTENT_WRP_ID } from '@framework/ui/Templates/ContentWrp/constants';
import IconClose from '@dealroadshow/uikit-icon/IconClose';
import { useHeaderBannerContext } from './HeaderBannerContext';
import { getContainer } from '@framework/di/getContainer';
import BannersRepository from '@condor/banners/BannersRepository';
import useElementSize from '@framework/hooks/useElementSize';
import { ANIMATION_SPEED } from './contants';
import style from './headerBanner.module.scss';

interface IProps {
  onClose?: () => void,
  afterClose?: () => void,
  isSticky?: boolean,
  shouldUseContentWrapAsScrollable?: boolean,
}

/**
 * @deprecated This component has been chosen for migration to UIKit, meaning any further changes or updates
 * must be completed within the migration process.
 * @see https://www.notion.so/finsight-group/Component-Migration-Process-f4475950481d429ba0dc450d0bb0cb8b
 */
const HeaderBanner = ({
  onClose = noop,
  afterClose = noop,
  isSticky = true,
  shouldUseContentWrapAsScrollable = false,
}: IProps) => {
  const bannerRepository = getContainer().get(BannersRepository);
  const [isFixed, setIsFixed] = useState(false);
  const { height: bannerHeight } = useElementSize('banner');

  const { hash, color, content } = useHeaderBannerContext();

  const [isShowBanner, setIsShowBanner] = useState(false);

  const scrollHandler = useCallback(throttle(() => {
    setIsFixed(window.scrollY > 0);
  }, 100), []);

  const wrapperScrollHandler = useCallback(throttle(() => {
    const el = document.getElementById(CONTENT_WRP_ID);
    // @ts-ignore
    setIsFixed(el.scrollTop > 0);
  }, 100), []);

  useEffect(() => {
    if (isSticky && content) {
      if (shouldUseContentWrapAsScrollable) {
        const el = document.getElementById(CONTENT_WRP_ID);
        el?.addEventListener('scroll', wrapperScrollHandler);
      } else {
        window.addEventListener('scroll', scrollHandler);
      }
    }
    return () => {
      if (shouldUseContentWrapAsScrollable) {
        const el = document.getElementById(CONTENT_WRP_ID);
        if (el) {
          el.removeEventListener('scroll', wrapperScrollHandler);
        }
      } else {
        window.removeEventListener('scroll', scrollHandler);
      }
    };
  }, [isSticky, content, shouldUseContentWrapAsScrollable]);

  useEffect(() => {
    bannerRepository.getClosedBanners().then((hashes) => {
      if (!hashes.includes(hash)) {
        setIsShowBanner(true);
      }
    });
  }, [hash]);

  const closeCallback = () => {
    onClose();
    setTimeout(() => {
      afterClose();
    }, ANIMATION_SPEED);
  };

  const onCloseBanner = useCallback(async () => {
    setIsShowBanner(false);
    closeCallback();
    await bannerRepository.setClosedBanners(hash);
  }, [onClose, afterClose, hash]);

  const bannerContent = useMemo(() => (
    <div className={ style.ieWrapper } id="banner">
      <div
        className={ cn(style.container, { [style.closedBanner]: !isShowBanner }) }
        style={ { backgroundColor: color } }
      >
        <div
          data-test="bannerContent"
          className={ style.content }
          dangerouslySetInnerHTML={ { __html: content } }
        />
        <IconClose
          className={ style.closeIcon }
          onClick={ onCloseBanner }
          data-test="closeBannerIcon"
        />
      </div>
    </div>
  ), [isShowBanner, content, onCloseBanner]);

  if (!content) {
    return null;
  }

  return (
    <>
      { isFixed && <div style={ { height: bannerHeight } } /> }
      <div
        className={ cn(style.headerBanner, {
          [style.headerBannerFixed]: isFixed,
        }) }
        data-test="headerBanner"
      >
        { bannerContent }
      </div>
    </>
  );
};

export default HeaderBanner;
